import React, { useCallback, ReactElement } from 'react';
import {
  AutoSizer,
  type AutoSizerChildProps,
} from 'react-virtualized-auto-sizer';
import { RowComponentProps, List } from 'react-window';
import { MENU_ITEM_HEIGHT } from '@shared/constants/misc.constants';
import { AUTO_SIZER_STYLES } from '@client/style/style.constants';
import { guards } from '@shared/helpers/typeguards.helpers';

interface VirtualizedItemsProps<T> {
  items: T[];
  children: (props: RowComponentProps) => React.ReactNode;
}

const VirtualizedItems = <T,>({
  items,
  children,
}: VirtualizedItemsProps<T>) => {
  // react-window List.rowComponent must return ReactElement | null, not ReactNode
  const rowComponent = useCallback(
    (props: RowComponentProps): ReactElement | null => {
      const node = children(props);

      // Return null if the node is undefined, null or false
      if (!guards.is.defined(node) || node === false) return null;
      return <>{node}</>;
    },
    [children],
  );

  const fixedSizeRenderer: (size: AutoSizerChildProps) => ReactElement =
    useCallback(
      ({ height, width }) => {
        return (
          <List
            style={{ height: height || '0', width: width || '0' }}
            rowHeight={MENU_ITEM_HEIGHT}
            rowCount={items.length}
            rowComponent={rowComponent}
            rowProps={{}}
          />
        );
      },
      [items.length, rowComponent],
    );

  return <AutoSizer style={AUTO_SIZER_STYLES} renderProp={fixedSizeRenderer} />;
};

export default VirtualizedItems;
